import { useFrontendAuth } from '../hooks/useAuth';
import { getMyChildren } from '../services/api.shop';
import { ChildDto } from '../types';
import { useTranslation } from '../context/TranslationContext';
import { useProtectedQuery } from '../hooks/useProtectedQuery';
import { translateWithFallback } from '../utils/translateWithFallback';

function ageYearsOnBirthday(birthDateIso: string, today: Date): number {
    const birth = new Date(birthDateIso);
    let years = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) years--;
    return Math.max(0, years);
}

function ordinal(n: number): string {
    const j = n % 10;
    const k = n % 100;
    if (j === 1 && k !== 11) return `${n}st`;
    if (j === 2 && k !== 12) return `${n}nd`;
    if (j === 3 && k !== 13) return `${n}rd`;
    return `${n}th`;
}

/** Soft confetti dots for the Figma-style birthday band */
function ConfettiDots() {
    const dots = [
        { t: '12%', l: '8%', c: '#F472B6', s: 6 },
        { t: '28%', l: '18%', c: '#FBBF24', s: 5 },
        { t: '18%', l: '78%', c: '#60A5FA', s: 6 },
        { t: '55%', l: '88%', c: '#A78BFA', s: 5 },
        { t: '70%', l: '12%', c: '#34D399', s: 4 },
        { t: '40%', l: '92%', c: '#FB7185', s: 4 },
        { t: '75%', l: '72%', c: '#FCD34D', s: 5 },
        { t: '22%', l: '42%', c: '#38BDF8', s: 3 },
        { t: '62%', l: '38%', c: '#C084FC', s: 4 },
        { t: '35%', l: '58%', c: '#F9A8D4', s: 3 },
    ];
    return (
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
            {dots.map((d, i) => (
                <span
                    key={i}
                    className="absolute rounded-full opacity-80"
                    style={{
                        top: d.t,
                        left: d.l,
                        width: d.s,
                        height: d.s,
                        backgroundColor: d.c,
                    }}
                />
            ))}
            <span className="absolute left-[15%] top-[20%] text-sm opacity-70">🎁</span>
            <span className="absolute right-[18%] top-[25%] text-sm opacity-70">✨</span>
            <span className="absolute bottom-[22%] left-[25%] text-sm opacity-70">🎈</span>
            <span className="absolute bottom-[28%] right-[22%] text-sm opacity-70">🎉</span>
        </div>
    );
}

const BirthdayBanner = () => {
    const { isAuthenticated, isAuthReady } = useFrontendAuth();
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);

    const { data: children = [] } = useProtectedQuery({
        queryKey: ['myChildren'],
        enabled: isAuthReady && isAuthenticated,
        queryFn: () => getMyChildren().then((res) => res.data),
        staleTime: 1000 * 60 * 5,
    });

    const today = new Date();
    const todayMonthDay = `${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`;

    const birthdayKids = children.filter((c: ChildDto) => {
        if (c.birthDate) {
            const birth = new Date(c.birthDate);
            const birthMonthDay = `${(birth.getMonth() + 1).toString().padStart(2, '0')}-${birth.getDate().toString().padStart(2, '0')}`;
            return birthMonthDay === todayMonthDay;
        }
        if (c.expectedBirthDate) {
            const expDate = c.expectedBirthDate.split('T')[0];
            return expDate === today.toISOString().split('T')[0];
        }
        return false;
    });

    if (!isAuthReady || !isAuthenticated || birthdayKids.length === 0) return null;

    return (
        <div className="relative w-full overflow-hidden border-b border-[color-mix(in_srgb,#E8D5B5,transparent_40%)] bg-gradient-to-r from-[#FFF8EB] via-[#FFF5E6] to-[#FFF0F5] py-8 sm:py-10">
            <ConfettiDots />
            <div className="relative z-10 mx-auto max-w-3xl px-4 text-center">
                <div className="mb-2 text-4xl sm:text-5xl" aria-hidden>
                    🎂
                </div>
                {birthdayKids.map((kid) => {
                    const years =
                        kid.birthDate && !kid.expectedBirthDate
                            ? ageYearsOnBirthday(kid.birthDate, today)
                            : null;
                    const title =
                        years != null && years > 0
                            ? tr('banner.happyBirthday.years', 'Happy {ordinal} birthday, {name}!')
                                  .replace('{ordinal}', ordinal(years))
                                  .replace('{name}', kid.name)
                            : tr('banner.happyBirthday', 'Happy birthday, {name}!').replace(
                                  '{name}',
                                  kid.name
                              );

                    return (
                        <div key={kid.id} className="mb-3 last:mb-0">
                            <h2 className="text-2xl font-bold tracking-tight text-[#5C4033] sm:text-3xl md:text-4xl">
                                {title}
                            </h2>
                            <p className="mt-2 text-sm text-[#7A5C4A]/sm:text-base">
                                {tr(
                                    'banner.specialDay',
                                    'Today is a very special day ☀️'
                                )}
                            </p>
                            <p className="mt-1 text-sm text-[#8B6F5C] sm:text-base">
                                {tr(
                                    'banner.wish',
                                    'Wishing you all the joy, laughter and love in the world!'
                                )}
                            </p>
                        </div>
                    );
                })}
                <div className="mt-3 flex justify-center gap-2 text-lg" aria-hidden>
                    <span>🎈</span>
                    <span>🎉</span>
                    <span>🎁</span>
                    <span>✨</span>
                    <span>🥳</span>
                </div>
            </div>
        </div>
    );
};

export default BirthdayBanner;
